import { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack } from '@mui/material';

import ListFilters from '@components/ListFilters/ListFilters';

import GranteeFilter, { GRANTEE_FILTER_LABEL, GranteeFilterOption } from './AccessFlowsFilters/GranteeFilter';
import IntegrationFilter, {
  INTEGRATION_FILTER_LABEL,
  IntegrationFilterOption,
} from './AccessFlowsFilters/IntegrationFilter';
import StatusFilter, { STATUS_FILTER_LABEL } from './AccessFlowsFilters/StatusFilter';
import ResourceTypeFilter, {
  RESOURCE_TYPE_LABEL,
  ResourceTypeFilterOption,
} from '@common/organisms/CommonFilters/ResourceTypeFilter';
import BundleFilter, { BUNDLE_FILTER_LABEL, BundleFilterOption } from './AccessFlowsFilters/BundleFilter';
import LabelFilter, { LABEL_FILTER_LABEL, LabelFilterOption } from './AccessFlowsFilters/LabelFilter';
import ApprovalFilter, { APPROVAL_FILTER_LABEL } from './AccessFlowsFilters/ApprovalFilter';
import TriggerTypeFilter, { TRIGGER_TYPE_FILTER_LABEL } from './AccessFlowsFilters/TriggerTypeFilter';
import {
  APPROVAL_FILTER_PARAM,
  BUNDLE_FILTER_PARAM,
  GRANTEE_FILTER_PARAM,
  INTEGRATION_FILTER_PARAM,
  LABEL_FILTER_PARAM,
  STATUS_FILTER_PARAM,
  TRIGGER_TYPE_FILTER_PARAM,
} from '@AccessFlows/common/constants';
import { RESOURCE_TYPE_FILTER_PARAM } from '@common/constants';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils';

export interface AccessFlowsFiltersOptions {
  grantee: GranteeFilterOption[];
  integration: IntegrationFilterOption[];
  resourceType: ResourceTypeFilterOption[];
  bundle: BundleFilterOption[];
  label: LabelFilterOption[];
}

interface AccessFlowsFiltersProps {
  options: AccessFlowsFiltersOptions;
}

export default function AccessFlowsFilters({ options }: AccessFlowsFiltersProps) {
  const { isEnabled: complexEnabled } = useFlagFixed(Flag.COMPLEX_GRANTEES_CONDITION);

  const [searchParams, setSearchParams] = useSearchParams();
  const [openedFilter, setOpenedFilter] = useState<string | undefined>(undefined);

  const filters = useMemo(() => {
    const baseFilters = [];

    if (!complexEnabled) {
      baseFilters.push({
        name: GRANTEE_FILTER_LABEL,
        queryParam: GRANTEE_FILTER_PARAM,
        element: <GranteeFilter options={options.grantee} opened={openedFilter === GRANTEE_FILTER_PARAM} />,
      });
    }

    return [
      ...baseFilters,
      {
        name: INTEGRATION_FILTER_LABEL,
        queryParam: INTEGRATION_FILTER_PARAM,
        element: <IntegrationFilter options={options.integration} opened={openedFilter === INTEGRATION_FILTER_PARAM} />,
      },
      {
        name: RESOURCE_TYPE_LABEL,
        queryParam: RESOURCE_TYPE_FILTER_PARAM,
        element: (
          <ResourceTypeFilter options={options.resourceType} opened={openedFilter === RESOURCE_TYPE_FILTER_PARAM} />
        ),
      },
      {
        name: BUNDLE_FILTER_LABEL,
        queryParam: BUNDLE_FILTER_PARAM,
        element: <BundleFilter options={options.bundle} opened={openedFilter === BUNDLE_FILTER_PARAM} />,
      },
      {
        name: STATUS_FILTER_LABEL,
        queryParam: STATUS_FILTER_PARAM,
        element: <StatusFilter opened={openedFilter === STATUS_FILTER_PARAM} />,
      },
      {
        name: LABEL_FILTER_LABEL,
        queryParam: LABEL_FILTER_PARAM,
        element: <LabelFilter options={options.label} opened={openedFilter === LABEL_FILTER_PARAM} />,
      },
      {
        name: APPROVAL_FILTER_LABEL,
        queryParam: APPROVAL_FILTER_PARAM,
        element: <ApprovalFilter opened={openedFilter === APPROVAL_FILTER_PARAM} />,
      },
      {
        name: TRIGGER_TYPE_FILTER_LABEL,
        queryParam: TRIGGER_TYPE_FILTER_PARAM,
        element: <TriggerTypeFilter opened={openedFilter === TRIGGER_TYPE_FILTER_PARAM} />,
      },
    ];
  }, [complexEnabled, openedFilter, options]);

  const [visibleFilters, setVisibleFilters] = useState<string[]>(() => {
    const defaultFilters = [GRANTEE_FILTER_PARAM, INTEGRATION_FILTER_PARAM, RESOURCE_TYPE_FILTER_PARAM];

    const searchParamsVisibleFilters = [];
    for (const filter of filters) {
      if (searchParams.has(filter.queryParam) && !defaultFilters.includes(filter.queryParam)) {
        searchParamsVisibleFilters.push(filter.queryParam);
      }
    }

    return [...defaultFilters, ...searchParamsVisibleFilters];
  });

  const hasActiveFilters = useMemo(() => {
    for (const filter of filters) {
      if (searchParams.has(filter.queryParam)) {
        return true;
      }
    }
    return false;
  }, [filters, searchParams]);

  const handleOnClear = useCallback(() => {
    for (const filter of filters) {
      searchParams.delete(filter.queryParam);
    }
    setSearchParams(searchParams);
  }, [filters, searchParams, setSearchParams]);

  const handleOnOnAddFilter = (qp: string) => {
    setVisibleFilters((prev) => [...prev, qp]);
    setOpenedFilter(qp);
  };

  return (
    <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
      <ListFilters
        visibleFilters={visibleFilters}
        filters={filters}
        onAddFilter={handleOnOnAddFilter}
        onClear={hasActiveFilters ? handleOnClear : undefined}
      />
    </Stack>
  );
}
